/**
 * Formatting handlers for text content (underline, highlight, lists, links, etc.)
 */

/**
 * Apply format to selected text
 */
export const applyFormatToSelection = (formatFn) => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  if (!range || range.collapsed) return false;

  const selectedText = range.toString();
  const selectedEl =
    range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;

  const span = document.createElement("span");
  formatFn(span, selectedEl);
  span.textContent = selectedText;

  range.deleteContents();
  range.insertNode(span);

  selection.removeAllRanges();
  const newRange = document.createRange();
  newRange.selectNodeContents(span);
  newRange.collapse(false);
  selection.addRange(newRange);

  return true;
};

/**
 * Update formatted text state
 */
export const createUpdateFormattedText = ({
  setTexts,
  setSelectedText,
  saveToHistory,
  isElementBeingEdited,
  setPendingChanges,
}) => {
  return (textId, type, updateFn) => {
    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    let el;
    if (type === "content") {
      el = document.getElementById(`content-div-${textId}`);
    } else if (type === "note") {
      el = document.getElementById(`note-div-${textId}`);
    } else if (type === "task") {
      el = document.getElementById(`task-div-${textId}`);
    }

    if (el) {
      let content = el.innerHTML || "";
      content = content.replace(/&nbsp;/g, " ");

      const plainText = el.innerText || el.textContent || "";
      const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);

      // Ensure changes are visibly applied
      el.innerHTML = content;

      updateFn({ content, plainText, isHtml });

      setTexts((prev) =>
        prev.map((t) =>
          t.id === textId
            ? {
                ...t,
                [type]: content,
                [`${type}PlainText`]: plainText,
                [`${type}IsHtml`]: isHtml,
                _lastUpdated: Date.now(),
              }
            : t
        )
      );

      setSelectedText((prev) =>
        prev?.id === textId
          ? {
              ...prev,
              [type]: content,
              [`${type}PlainText`]: plainText,
              [`${type}IsHtml`]: isHtml,
              _lastUpdated: Date.now(),
            }
          : prev
      );

      saveToHistory();
    }
  };
};

/**
 * Handle list key down (auto-continue bullets/numbers)
 */
export const createHandleListKeyDown = (placeCaretAtEnd) => {
  return (e) => {
    if (e.key !== "Enter") return;

    const targetEl = e.currentTarget;

    setTimeout(() => {
      if (!targetEl) return;

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      let currentNode = sel.anchorNode;
      while (currentNode && currentNode !== targetEl) {
        if (
          currentNode.nodeType === Node.ELEMENT_NODE &&
          /^(DIV|LI|P)$/.test(currentNode.nodeName)
        ) {
          break;
        }
        currentNode = currentNode.parentNode;
      }
      if (!currentNode || currentNode === targetEl) return;
      if (currentNode.nodeName === "LI") return;

      let prev = currentNode.previousSibling;
      while (prev && prev.nodeType === Node.TEXT_NODE && !prev.textContent) {
        prev = prev.previousSibling;
      }
      if (!prev) return;

      const prevText = (prev.textContent || "").trim();
      const bulletPrefix = "• ";

      if (prevText.startsWith(bulletPrefix)) {
        if (currentNode.textContent.trim() === "") {
          currentNode.textContent = bulletPrefix;
          placeCaretAtEnd(currentNode);
        }
      } else {
        const numberMatch = prevText.match(/^(\d+)\.\s+/);
        if (numberMatch) {
          const nextNum = parseInt(numberMatch[1], 10) + 1;
          if (currentNode.textContent.trim() === "") {
            currentNode.textContent = `${nextNum}. `;
            placeCaretAtEnd(currentNode);
          }
        }
      }

      const inputEvent = new Event("input", { bubbles: true });
      if (targetEl.dispatchEvent) {
        targetEl.dispatchEvent(inputEvent);
      }
    }, 0);
  };
};
